import "./CreateSeasonalHoursPopup.scss";
import { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import { toast } from "react-toastify";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";
import {
  ISeasonalHoursFacility,
  IWeekdayHoursFacility,
} from "../../../types/facility";
import { createSeasonalHours } from "../../../api/facility/facilityOpeningTimes";
import WeekDaySelector from "../../../hoc/DatePicker/Datepicker";

type FacilityCourtType = "INDOOR" | "OUTDOOR" | "GYM" | "REHAB";

// The same season is nearly always wanted for several court types at once,
// so creation is no longer locked to the tab that is open.
const COURT_TYPE_OPTIONS: { value: FacilityCourtType; label: string }[] = [
  { value: "INDOOR", label: "Inomhus" },
  { value: "OUTDOOR", label: "Utomhus" },
  { value: "REHAB", label: "Rehab" },
];

interface ICreateSeasonalHoursPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function CreateSeasonalHoursPopup(props: ICreateSeasonalHoursPopupProps) {
  const { showPopup, onClose, onConfirm } = props;
  const [showHandleConfirmationPopup, setShowHandleConfirmationPopup] =
    useState(false);
  const [showDoubleConfirmationPopup, setShowDoubleConfirmationPopup] =
    useState(false);
  const handleConfirm = () => {
    onConfirm();
    handleClose();
  };
  const handleClose = () => {
    setSeasonalHourData(initialSeasonalData);
    setSelectedCourtTypes([]);

    setWeekdayHours(
      Object.entries(weekdays).map(([swedish, english]) => ({
        weekday: english,
        opening_hours: "07:00:00",
        closing_hours: "23:00:00",
        court_type: COURT_TYPE_OPTIONS[0].value,
      }))
    );
    onClose();
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [startTime, setStartTime] = useState("07:00:00");
  const [endTime, setEndTime] = useState("23:00:00");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const initialSeasonalData: ISeasonalHoursFacility = {
    alias: "",
    closed: false,
    end_date: "",
    start_date: "",
    court_type: COURT_TYPE_OPTIONS[0].value,
    weekday_hours: [],
  };

  const [seasonalHourData, setSeasonalHourData] =
    useState<ISeasonalHoursFacility>(initialSeasonalData);

  // Starts empty and has to be chosen. Defaulting to every court meant a
  // mis-click wrote three settings instead of one, and the writes are not
  // atomic — undoing that is three deletions across three lists.
  const [selectedCourtTypes, setSelectedCourtTypes] = useState<
    FacilityCourtType[]
  >([]);

  const toggleCourtType = (type: FacilityCourtType) => {
    setSelectedCourtTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const weekdays: Record<
    "Måndag" | "Tisdag" | "Onsdag" | "Torsdag" | "Fredag" | "Lördag" | "Söndag",
    string
  > = {
    Måndag: "monday",
    Tisdag: "tuesday",
    Onsdag: "wednesday",
    Torsdag: "thursday",
    Fredag: "friday",
    Lördag: "saturday",
    Söndag: "sunday",
  };

  const reverseWeekdays: Record<string, string> = {
    monday: "Måndag",
    tuesday: "Tisdag",
    wednesday: "Onsdag",
    thursday: "Torsdag",
    friday: "Fredag",
    saturday: "Lördag",
    sunday: "Söndag",
  };

  const [weekdayHours, setWeekdayHours] = useState<IWeekdayHoursFacility[]>(
    Object.entries(weekdays).map(([swedish, english]) => ({
      weekday: english,
      opening_hours: "07:00:00",
      closing_hours: "23:00:00",
      court_type: COURT_TYPE_OPTIONS[0].value,
    }))
  );
  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSeasonalHourData({
      ...seasonalHourData,
      [e.target.name]: e.target.value,
    });
  };
  const handleClosedToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isClosed = e.target.checked;

    setSeasonalHourData((prev) => ({
      ...prev,
      closed: isClosed,
    }));
  };
  const handleWeekdayTimeChange = (
    index: number,
    timeType: "opening_hours" | "closing_hours",
    e: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>
  ) => {
    let timeValue = e.target.value;

    // Handle blur logic
    if (e.type === "blur") {
      let parts = timeValue.split(":");

      if (parts.length === 2) {
        timeValue += ":00";
      } else if (parts.length === 1 && timeValue.includes(":")) {
        timeValue += "00:00";
      } else if (parts.length === 1) {
        timeValue = timeValue.padStart(2, "0") + ":00:00";
      }
    }

    if (e.type === "change") {
      if (timeValue === "24:00") {
        timeValue = "00:00";
      }
      let parts = timeValue.split(":");

      if (parts.length === 2) {
        timeValue += ":00";
      } else if (parts.length === 1 && timeValue.includes(":")) {
        timeValue += "00:00";
      } else if (parts.length === 1) {
        timeValue = timeValue.padStart(2, "0") + ":00:00";
      }
    }

    // Update the relevant weekday's time
    const updatedHours = [...weekdayHours];
    updatedHours[index][timeType] = timeValue;
    setWeekdayHours(updatedHours);
  };

  const handleCreateSeasonalHours = async () => {
    setLoading(true);

    for (const weekday of weekdayHours) {
      if (weekday.opening_hours > weekday.closing_hours) {
        toast.error(
          `Öppningstiden kan inte vara senare än stängningstiden  (${
            reverseWeekdays[weekday.weekday]
          })`
        );
        setLoading(false);
        return; // Exit the function
      }
    }
    if (startDate > endDate) {
      toast.error("Startdatum kan inte vara senare än slutdatum");
      setLoading(false);
      return;
    }
    const dataToCreate = {
      ...seasonalHourData,
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
      weekday_hours: weekdayHours,
    };
    if (dataToCreate.alias === "") {
      toast.error("Namn är obligatoriskt");
      setLoading(false);
      return;
    }
    if (selectedCourtTypes.length === 0) {
      toast.error("Välj minst en typ av bana");
      setLoading(false);
      return;
    }

    // One write per court type. These are not atomic — a single type can fail
    // (e.g. the alias already exists for it) while the others succeed, so
    // report per type instead of one blanket success.
    const succeeded: string[] = [];
    const failed: string[] = [];

    for (const type of selectedCourtTypes) {
      const label =
        COURT_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
      try {
        const response = await createSeasonalHours({
          ...dataToCreate,
          court_type: type,
          // The nested weekday rows carry their own court_type, so they have
          // to follow the record they belong to.
          weekday_hours: weekdayHours.map((wh) => ({
            ...wh,
            court_type: type,
          })),
        });
        if (response && !("code" in response)) {
          succeeded.push(label);
        } else {
          failed.push(label);
        }
      } catch (error: any) {
        failed.push(label);
      }
    }

    setLoading(false);

    if (succeeded.length > 0) {
      toast.success(`Avvikande inställning skapad: ${succeeded.join(", ")}`);
    }
    if (failed.length > 0) {
      toast.error(`Misslyckades för: ${failed.join(", ")}`);
    }
    if (succeeded.length > 0) {
      handleConfirm();
    }
  };

  useEffect(() => {
    if (showPopup) {
      setSeasonalHourData(initialSeasonalData);
      setSelectedCourtTypes([]);
    }
  }, [showPopup]);
  return (
    <Popup
      showPopup={props.showPopup}
      onClose={handleClose}
      leftTopElement={
        <span className="text-m">Skapa avvikande inställning</span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
            setShowHandleConfirmationPopup(true);
          }}
        >
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              <label htmlFor="alias" className="text-s">
                Namn
              </label>
              <input
                type="text"
                name="alias"
                value={seasonalHourData.alias}
                onChange={handleInputChange}
                className="text-m"
              />
              <label htmlFor="memberType" className="text-s">
                Start datum
              </label>
              <WeekDaySelector
                textM
                includeWeekdays={false}
                value={startDate}
                onChange={(date) => setStartDate(date)}
              />

              <label htmlFor="memberType" className="text-s">
                Slut datum
              </label>
              <WeekDaySelector
                textM
                includeWeekdays={false}
                value={endDate}
                onChange={(date) => setEndDate(date)}
              />

              <label htmlFor="courtType" className="text-s">
                Typ av bana
              </label>
              {COURT_TYPE_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className="general-popup-flex-between"
                >
                  <span className="text-m secondary-text">{option.label}</span>
                  <label className="toggle text-m">
                    <input
                      type="checkbox"
                      name={`court_type_${option.value}`}
                      checked={selectedCourtTypes.includes(option.value)}
                      onChange={() => toggleCourtType(option.value)}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              ))}
              <label htmlFor="closed" className="text-m">
                Ska det vara stängt under detta interval ?
              </label>
              <div className="general-popup-flex-between">
                <span className="text-m secondary-text">Stängt</span>
                <label className="toggle text-m">
                  <input
                    type="checkbox"
                    name="closed"
                    checked={seasonalHourData.closed}
                    onChange={(e) => handleClosedToggle(e)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>
          {!seasonalHourData.closed &&
            weekdayHours.map((weekdayData, index) => (
              <div key={index} className="general-popup-form-group">
                <div className="general-popup-form-field">
                  <div key={weekdayData.weekday} className="weekday-group">
                    <label className="text-s">
                      {reverseWeekdays[weekdayData.weekday]}
                    </label>
                    <div className="general-popup-flex-between">
                      <label className="text-m"> Start </label>
                      <input
                        type="time"
                        value={weekdayData.opening_hours}
                        onChange={(e) =>
                          handleWeekdayTimeChange(index, "opening_hours", e)
                        }
                        className="text-m"
                      />
                    </div>
                    <div className="general-popup-flex-between">
                      <label className="text-m">Slut </label>
                      <input
                        type="time"
                        value={weekdayData.closing_hours}
                        onChange={(e) =>
                          handleWeekdayTimeChange(index, "closing_hours", e)
                        }
                        className="text-m"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

          <div className="general-popup-bottom-buttons-wrapper flex-end">
            <button type="submit" className="text-m">
              Skapa avvikande inställning
            </button>
          </div>
        </form>
      </div>

      <HandleConfirmationPopup
        showPopup={showHandleConfirmationPopup}
        onClose={() => setShowHandleConfirmationPopup(false)}
        onConfirm={() => {
          setShowHandleConfirmationPopup(false);
          setShowDoubleConfirmationPopup(true);
        }}
        confirmText="Ja"
        questionText="Är du säker på att du vill skapa upp denna avvikande inställning?"
        denyText="Avbryt"
      />
      <HandleConfirmationPopup
        showPopup={showDoubleConfirmationPopup}
        onClose={() => setShowDoubleConfirmationPopup(false)}
        onConfirm={() => {
          handleCreateSeasonalHours();
          setShowDoubleConfirmationPopup(false);
        }}
        confirmText="Ja"
        questionText="Om bokningar ligger utanför uppdaterade öppettider kommer dessa inte avbokas. Vill du genomföra uppdateringen ändå?"
        denyText="Avbryt"
      />
    </Popup>
  );
}

export default CreateSeasonalHoursPopup;
